import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, BookOpen, Copy, Crown, Download, History, RefreshCw, Trash2, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import { snapdom } from '@zumer/snapdom';
import QRCode from 'qrcode';
import {
  activateMembershipCode,
  chatApi,
  fetchUserRecordCollection,
  getStoredUser,
  hasContentUnlock,
  lookupKangxiStrokesApi,
  saveStoredUser,
  saveUserRecordCollection,
  StoredUser,
} from '../lib/api';
import {
  buildProfessionalNameStudyCacheKey,
  buildProfessionalNameStudyFallback,
  buildProfessionalNameStudyUserMessage,
  getGridChartLuckText,
  getGridGenderLuckText,
  getShortGridLabel,
  getSimpleGridHeader,
  parseFullTextToDetails,
  PROFESSIONAL_NAME_STUDY_DEMO_REPORT,
  parseProfessionalNameStudyReport,
  PROFESSIONAL_NAME_STUDY_DEMO_AGE,
  PROFESSIONAL_NAME_STUDY_FEATURE_ID,
  PROFESSIONAL_NAME_STUDY_PROGRESS_STEPS,
  PROFESSIONAL_NAME_STUDY_TIME_ESTIMATE,
  PROFESSIONAL_NAME_STUDY_UNLOCK_ID,
  ProfessionalNameStudyMode,
  ProfessionalNameStudyReport,
  toDisplayLuckTag,
} from '../lib/professional-name-study';
import {
  buildStrokeSlots,
  calculateSancaiLiuge,
  GridResult,
  LuckLevel,
  SancaiGender,
  SancaiLiugeResult,
  StageLevel,
} from '../lib/sancai-liuge';
import AiTaskProgressOverlay from './AiTaskProgressOverlay';
import XuanYuanTopBar from './XuanYuanTopBar';
import XuanYuanReportFooter from './XuanYuanReportFooter';
import SancaiLiugeProReportPage from './SancaiLiugeProReportPage';

interface SancaiLiugeProps {
  onBack: () => void;
  currentUser?: StoredUser | null;
  memberActive?: boolean;
  onRequestProAccess?: () => void;
  /** 点击某字跳转到康熙字典查询 */
  onOpenKangxiDictionary?: (char: string) => void;
  /** 从康熙字典跳入时的预填姓名（单字填姓，多字按姓+名拆） */
  initialSurname?: string;
  initialGivenName?: string;
  pendingRecordId?: string | null;
  onPendingRecordConsumed?: (recordId: string) => void;
}

interface StoredRecord {
  id: string;
  createdAt: number;
  updatedAt: number;
  surname: string;
  givenName: string;
  gender: SancaiGender;
  age?: number | null;
  result: SancaiLiugeResult;
}

const STORAGE_KEY = 'qimen_sancai_liuge_records_v1';
const ONLINE_STROKE_CACHE_KEY = 'qimen_sancai_nameology_stroke_cache_v2';
const PROFESSIONAL_NAME_STUDY_PROGRESS_TICK_MS = 100;
const PROFESSIONAL_NAME_STUDY_PROGRESS_DURATION_MS = 68000;
const PROFESSIONAL_NAME_STUDY_MAX_RETRIES = 2;
const PROFESSIONAL_NAME_STUDY_RETRY_DELAY_MS = 1200;

const sortStoredRecordsByRecency = (records: StoredRecord[]): StoredRecord[] => (
  [...records].sort((a, b) => (
    Number(b.updatedAt || b.createdAt || 0) - Number(a.updatedAt || a.createdAt || 0)
    || Number(b.createdAt || 0) - Number(a.createdAt || 0)
    || String(a.id || '').localeCompare(String(b.id || ''), 'zh-CN')
  ))
);

const mergeStoredRecordsById = (records: StoredRecord[]): StoredRecord[] => {
  const map = new Map<string, StoredRecord>();
  records.forEach((record) => {
    const id = String(record?.id || '').trim();
    if (!id) return;
    const existing = map.get(id);
    const nextUpdatedAt = Number(record.updatedAt || record.createdAt || 0);
    const existingUpdatedAt = Number(existing?.updatedAt || existing?.createdAt || 0);
    if (!existing || nextUpdatedAt >= existingUpdatedAt) {
      map.set(id, {
        ...record,
        updatedAt: Number(record.updatedAt || record.createdAt || Date.now()),
      });
    }
  });
  return sortStoredRecordsByRecency(Array.from(map.values()));
};

const loadRecords = (): StoredRecord[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredRecord[];
    if (!Array.isArray(parsed)) return [];
    return mergeStoredRecordsById(
      parsed
        .filter((item) => item && item.id && item.result)
        .map((item) => ({
          ...item,
          updatedAt: Number(item.updatedAt || item.createdAt || Date.now()),
        })),
    );
  } catch {
    return [];
  }
};

const saveRecords = (records: StoredRecord[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(mergeStoredRecordsById(records)));
  } catch {
    // noop
  }
};

const loadOnlineStrokeCache = (): Record<string, number> => {
  try {
    const raw = localStorage.getItem(ONLINE_STROKE_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return {};
    const map: Record<string, number> = {};
    Object.entries(parsed).forEach(([char, value]) => {
      const stroke = Number(value);
      if (char.length === 1 && Number.isFinite(stroke) && stroke > 0) {
        map[char] = stroke;
      }
    });
    return map;
  } catch {
    return {};
  }
};

const saveOnlineStrokeCache = (map: Record<string, number>): void => {
  try {
    localStorage.setItem(ONLINE_STROKE_CACHE_KEY, JSON.stringify(map));
  } catch {
    // noop
  }
};

const formatDate = (ts: number): string =>
  new Date(ts).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

const stageStyle = (level: StageLevel): string => {
  if (level === '大吉') return 'bg-emerald-100 text-emerald-800 border-emerald-300';
  if (level === '吉') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (level === '平') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (level === '小凶') return 'bg-orange-50 text-orange-700 border-orange-200';
  if (level === '凶') return 'bg-rose-50 text-rose-700 border-rose-200';
  return 'bg-slate-100 text-slate-600 border-slate-300';
};

const sanitizeName = (value: string): string => {
  const chars = value.match(/[\u3400-\u9fff]/g) || [];
  return chars.join('').slice(0, 2);
};

const GRID_ORDER: GridResult['key'][] = ['tiange', 'renge', 'dige', 'biange', 'waige', 'zongge'];

const KAITI_FONT = "font-['Kaiti','STKaiti','KaiTi_GB2312','楷体','serif']";
/** 图标响应式尺寸：随视口宽度变化，手机到 pad 更协调 */
const ICON_SIZE = 'w-[clamp(1rem,3.5vw,1.5rem)] h-[clamp(1rem,3.5vw,1.5rem)]';
const SERIF_NUM_FONT = "font-['SimSun','Songti_SC','STSong','serif']";

const getRatingColor = (rating: string): string => {
  const s = String(rating);
  if (s === '半吉' || s === '半凶') return 'text-[#D08A45]';
  if (s.includes('大吉') || (s === '吉') || (s.includes('吉') && !s.includes('凶'))) return 'text-[#528A5B]';
  if (s.includes('大凶') || (s === '凶') || (s.includes('凶') && !s.includes('吉'))) return 'text-[#C24B4B]';
  return 'text-[#D08A45]';
};

const getRatingBg = (levelOrTag: LuckLevel | string): string => {
  const s = String(levelOrTag);
  if (s === '半吉' || s === '半凶') return 'bg-[#D08A45]';
  if (s === '吉' || (s.includes('吉') && !s.includes('凶'))) return 'bg-[#528A5B]';
  if (s === '凶' || (s.includes('凶') && !s.includes('吉'))) return 'bg-[#C24B4B]';
  return 'bg-[#D08A45]';
};

const getGridAnalysisBadgeText = (grid: GridResult): string => {
  const genderLuck = String(getGridGenderLuckText(grid) || '').trim();
  const compactGenderLuck = genderLuck.replace(/\s+/g, '');
  if (compactGenderLuck === '男女皆吉' || compactGenderLuck === '男女均吉') return '吉';
  if (compactGenderLuck === '男女皆凶' || compactGenderLuck === '男女均凶') return '凶';
  if (compactGenderLuck.startsWith('男女皆') || compactGenderLuck.startsWith('男女均')) {
    const tail = compactGenderLuck.replace(/^男女[皆均]/, '').trim();
    if (tail) return tail;
  }
  if (compactGenderLuck.includes('男') && compactGenderLuck.includes('女')) {
    const normalized = compactGenderLuck.replace(/^男女吉凶[:：]?/, '').trim();
    return normalized || compactGenderLuck;
  }
  const chartLuck = String(getGridChartLuckText(grid) || '').trim();
  if (chartLuck) return chartLuck;
  return toDisplayLuckTag(grid.luckTag81 || grid.luckLevel);
};

const splitBadgeLines = (value: string): string[] => {
  const compact = String(value || '').replace(/\s+/g, '').trim();
  if (!compact) return [''];
  const genderLines = compact.match(/男[^男女]+|女[^男女]+/g);
  if (genderLines && genderLines.length >= 2) return genderLines;
  const chars = Array.from(compact);
  if (chars.length <= 2) return [compact];
  if (chars.length <= 6) {
    const firstLineCount = Math.ceil(chars.length / 2);
    return [
      chars.slice(0, firstLineCount).join(''),
      chars.slice(firstLineCount).join(''),
    ].filter(Boolean);
  }
  const chunk = Math.ceil(chars.length / 3);
  return [
    chars.slice(0, chunk).join(''),
    chars.slice(chunk, chunk * 2).join(''),
    chars.slice(chunk * 2).join(''),
  ].filter(Boolean);
};

const splitDetailParagraphs = (value: string, maxCharsPerParagraph = 68): string[] => {
  const text = String(value || '').replace(/\r/g, '').trim();
  if (!text) return [];
  const manualParagraphs = text
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (manualParagraphs.length > 1) return manualParagraphs;
  if (text.length <= maxCharsPerParagraph) return [text];

  const sentences = (text.match(/[^。！？!?；;\n]+[。！？!?；;]?/g) || [])
    .map((item) => item.trim())
    .filter(Boolean);
  if (sentences.length <= 1) return [text];

  const paragraphs: string[] = [];
  let current = '';
  sentences.forEach((sentence) => {
    if (!current) {
      current = sentence;
      return;
    }
    if ((current + sentence).length <= maxCharsPerParagraph) {
      current += sentence;
      return;
    }
    paragraphs.push(current);
    current = sentence;
  });
  if (current) paragraphs.push(current);
  return paragraphs;
};

const DiamondDivider = () => (
  <div className="my-6 flex items-center justify-center gap-1.5">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="flex h-2 w-2 rotate-45 items-center justify-center border border-[#C87635]">
        <div className="h-0.5 w-0.5 bg-[#C87635]" />
      </div>
    ))}
  </div>
);

const SectionTitle: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div className="relative my-5">
    <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-[#D8C1AA] to-transparent" />
    <div className="relative mx-auto w-fit rounded-full border border-[#DCC7B2] bg-gradient-to-r from-[#FDF9F2] via-[#FFFDF9] to-[#FDF9F2] px-5 py-2 shadow-[0_4px_14px_rgba(148,87,67,0.08)] sm:px-7">
      <div className="flex items-center gap-2">
        <span className="text-xs text-[#C87635]">✦</span>
        <h2 className={`text-[18px] font-bold tracking-[0.16em] text-[#4A3C31] ${KAITI_FONT}`}>{title}</h2>
        <span className="text-xs text-[#C87635]">✦</span>
      </div>
      {subtitle ? <p className="mt-1 text-center text-xs tracking-wide text-[#8B6B5A] font-sans">{subtitle}</p> : null}
    </div>
  </div>
);

const PRO_TEACHER_WECHAT = 'dao6257';
const PRO_TEACHER_QR_SRC = '/contact/ruoxuan-wechat-qr.png';
const PERMANENT_ADMIN_EMAIL = '930198090@qq.com';

const normalizeAgeInput = (raw: string): string => String(raw || '').replace(/[^\d]/g, '').slice(0, 3);

const parseAgeValue = (raw: string): number | null => {
  const next = Number(normalizeAgeInput(raw));
  if (!Number.isFinite(next) || next <= 0 || next > 120) return null;
  return Math.floor(next);
};

const formatAgeLabel = (raw: string): string => {
  const age = parseAgeValue(raw);
  return age ? `${age}岁` : '未填写';
};

const normalizeAdminIdentity = (raw: string | null | undefined): string => String(raw || '').trim().toLowerCase();

const isPermanentAdminUser = (user: StoredUser | null | undefined): boolean => {
  if (!user) return false;
  const uid = normalizeAdminIdentity(user.userId || user.email || '');
  return uid === PERMANENT_ADMIN_EMAIL;
};

const isAdminMember = (user: StoredUser | null | undefined): boolean => (
  Boolean(user?.isAdminMember || user?.memberType === 'admin' || isPermanentAdminUser(user))
);

type ContentView = 'input' | 'result' | 'records' | 'proReport' | 'proExport';
type ViewMode = ContentView;

type FrameSlotKey = 'top1' | 's1' | 's2' | 'g1' | 'g2' | 'bottom1';
type NameFrameLayout = 'single_single' | 'single_double' | 'double_single' | 'double_double';

interface FrameTemplate {
  key: NameFrameLayout;
  label: string;
  surnameCount: 1 | 2;
  givenCount: 1 | 2;
  slotOrder: FrameSlotKey[];
  rowPositions: number[];
  frameTop: number;
  frameBottom: number;
}

interface FrameCellData {
  kind: 'real' | 'virtual';
  char: string;
  stroke: number;
}

interface FrameGridData {
  value: string;
  rating: string;
  wuxing: string;
}

type FrameGridKey = GridResult['key'];
type SideGridKey = Exclude<FrameGridKey, 'zongge'>;

const FRAME_LAYOUT_ORDER: NameFrameLayout[] = [
  'single_single',
  'single_double',
  'double_single',
  'double_double',
];

const FRAME_TEMPLATES: Record<NameFrameLayout, FrameTemplate> = {
  single_single: {
    key: 'single_single',
    label: '单姓 + 单名',
    surnameCount: 1,
    givenCount: 1,
    slotOrder: ['top1', 's1', 'g1', 'bottom1'],
    rowPositions: [24, 38, 52, 66],
    frameTop: 16,
    frameBottom: 74,
  },
  single_double: {
    key: 'single_double',
    label: '单姓 + 双名',
    surnameCount: 1,
    givenCount: 2,
    slotOrder: ['top1', 's1', 'g1', 'g2'],
    rowPositions: [24, 38, 52, 66],
    frameTop: 16,
    frameBottom: 74,
  },
  double_single: {
    key: 'double_single',
    label: '双姓 + 单名',
    surnameCount: 2,
    givenCount: 1,
    slotOrder: ['s1', 's2', 'g1', 'bottom1'],
    rowPositions: [24, 38, 52, 66],
    frameTop: 16,
    frameBottom: 74,
  },
  double_double: {
    key: 'double_double',
    label: '双姓 + 双名',
    surnameCount: 2,
    givenCount: 2,
    slotOrder: ['s1', 's2', 'g1', 'g2'],
    rowPositions: [24, 38, 52, 66],
    frameTop: 16,
    frameBottom: 74,
  },
};

const FRAME_TEMPLATE_NOTES: Record<NameFrameLayout, string> = {
  single_single: '单姓单名：顶部与底部各补一个虚位“1”。',
  single_double: '单姓双名：顶部补一个虚位“1”。',
  double_single: '双姓单名：底部补一个虚位“1”。',
  double_double: '双姓双名：不补虚位“1”。',
};

const makeVirtualCell = (): FrameCellData => ({
  kind: 'virtual',
  char: '',
  stroke: 1,
});

const createDefaultFrameCells = (): Record<FrameSlotKey, FrameCellData> => ({
  top1: makeVirtualCell(),
  s1: { kind: 'real', char: '', stroke: 0 },
  s2: { kind: 'real', char: '', stroke: 0 },
  g1: { kind: 'real', char: '', stroke: 0 },
  g2: { kind: 'real', char: '', stroke: 0 },
  bottom1: makeVirtualCell(),
});

const getFrameLayout = (surnameCount: number, givenCount: number): NameFrameLayout => {
  if (surnameCount === 1 && givenCount === 1) return 'single_single';
  if (surnameCount === 1 && givenCount === 2) return 'single_double';
  if (surnameCount === 2 && givenCount === 1) return 'double_single';
  return 'double_double';
};

const getFrameGridPairs = (
  surnameCount: 1 | 2,
  givenCount: 1 | 2,
): Record<SideGridKey, [FrameSlotKey, FrameSlotKey]> => ({
  tiange: surnameCount === 1 ? ['top1', 's1'] : ['s1', 's2'],
  renge: surnameCount === 1 ? ['s1', 'g1'] : ['s2', 'g1'],
  dige: givenCount === 1 ? ['g1', 'bottom1'] : ['g1', 'g2'],
  biange: surnameCount === 1
    ? ['s1', givenCount === 1 ? 'g1' : 'g2']
    : ['s2', givenCount === 1 ? 'g1' : 'g2'],
  waige:
    surnameCount === 1 && givenCount === 1
      ? ['top1', 'bottom1']
      : surnameCount === 1
        ? ['top1', 'g2']
        : givenCount === 1
          ? ['s1', 'bottom1']
          : ['s1', 'g2'],
});

/** SlotNode：字格节点（虚位显示 1，实位显示字+笔画），响应式适配手机/平板/电脑 */
const SlotNode: React.FC<{
  top: number;
  charData: { char: string; stroke: string; isDummy: boolean };
}> = ({ top, charData }) => (
  <>
    {charData.isDummy ? (
      <div className={`absolute z-30 -translate-x-1/2 -translate-y-1/2 text-[30px] sm:text-[32px] md:text-[28px] lg:text-[30px] font-bold text-[#8B7D72] ${SERIF_NUM_FONT}`} style={{ top: `${top}%`, left: '47%' }}>
        1
      </div>
    ) : (
      <>
        <div className={`absolute z-30 -translate-x-1/2 -translate-y-1/2 text-[56px] sm:text-[60px] md:text-[52px] lg:text-[56px] font-bold text-[#4A3C31] ${KAITI_FONT}`} style={{ top: `${top}%`, left: '47%' }}>
          {charData.char}
        </div>
        <div className={`absolute z-30 -translate-y-1/2 text-[30px] sm:text-[32px] md:text-[28px] lg:text-[30px] font-bold text-[#8B7D72] ${SERIF_NUM_FONT}`} style={{ top: `${top}%`, left: '53%' }}>
          {charData.stroke}
        </div>
      </>
    )}
  </>
);

/** 右侧括号组件，严格按规范 */
const RightBracket: React.FC<{
  top: number;
  bottom: number;
  topStart: number;
  bottomStart: number;
  vLineRight: number;
  title: string;
  value: string;
  rating: string;
  wuxing: string;
}> = ({ top, bottom, topStart, bottomStart, vLineRight, title, value, rating, wuxing }) => {
  const mid = (top + bottom) / 2;
  return (
    <>
      <div className="absolute z-10 border-t-2 border-[#DBCAB8]" style={{ top: `${top}%`, left: `${topStart}%`, right: `${vLineRight}%` }} />
      <div className="absolute z-10 border-t-2 border-[#DBCAB8]" style={{ top: `${bottom}%`, left: `${bottomStart}%`, right: `${vLineRight}%` }} />
      <div className="absolute z-10 border-r-2 border-[#DBCAB8]" style={{ top: `${top}%`, bottom: `${100 - bottom}%`, right: `${vLineRight}%` }} />
      <div className="absolute z-10 border-t-2 border-[#DBCAB8]" style={{ top: `${mid}%`, right: `${vLineRight - 3}%`, width: '3%' }} />
      <div className="absolute z-20 flex flex-col justify-center whitespace-nowrap" style={{ top: `${mid}%`, left: `${100 - vLineRight + 3}%`, transform: 'translateY(-50%)' }}>
        <div className="flex items-center gap-2 md:gap-3">
          <span className={`text-[26px] sm:text-[28px] md:text-[24px] lg:text-[26px] font-bold text-[#4A3C31] ${KAITI_FONT}`}>{title}</span>
          <span className={`text-[30px] sm:text-[32px] md:text-[28px] lg:text-[30px] font-bold text-[#8B7D72] ${SERIF_NUM_FONT}`}>{value}</span>
        </div>
        <span className={`text-[20px] sm:text-[22px] md:text-[18px] lg:text-[20px] font-bold mt-1 md:mt-2 text-[#8B6B5A] ${KAITI_FONT}`}>
          （<span className={getRatingColor(rating)}>{rating}</span>·{wuxing}）
        </span>
      </div>
    </>
  );
};

/** 左侧括号组件，严格按规范，支持 textGap */
const LeftBracket: React.FC<{
  top: number;
  bottom: number;
  topStart: number;
  bottomStart: number;
  vLineLeft: number;
  title: string;
  value: string;
  rating: string;
  wuxing: string;
  textGap?: number;
}> = ({ top, bottom, topStart, bottomStart, vLineLeft, title, value, rating, wuxing, textGap = 3 }) => {
  const mid = (top + bottom) / 2;
  return (
    <>
      <div className="absolute z-10 border-t-2 border-[#DBCAB8]" style={{ top: `${top}%`, left: `${vLineLeft}%`, right: `${100 - topStart}%` }} />
      <div className="absolute z-10 border-t-2 border-[#DBCAB8]" style={{ top: `${bottom}%`, left: `${vLineLeft}%`, right: `${100 - bottomStart}%` }} />
      <div className="absolute z-10 border-l-2 border-[#DBCAB8]" style={{ top: `${top}%`, bottom: `${100 - bottom}%`, left: `${vLineLeft}%` }} />
      <div className="absolute z-10 border-t-2 border-[#DBCAB8]" style={{ top: `${mid}%`, left: `${vLineLeft - textGap}%`, width: `${textGap}%` }} />
      <div className="absolute z-20 flex flex-col items-end justify-center whitespace-nowrap" style={{ top: `${mid}%`, left: `${vLineLeft - textGap}%`, transform: 'translate(-100%, -50%)' }}>
        <div className="flex items-center gap-2 md:gap-3">
          <span className={`text-[30px] sm:text-[32px] md:text-[28px] lg:text-[30px] font-bold text-[#8B7D72] ${SERIF_NUM_FONT}`}>{value}</span>
          <span className={`text-[26px] sm:text-[28px] md:text-[24px] lg:text-[26px] font-bold text-[#4A3C31] ${KAITI_FONT}`}>{title}</span>
        </div>
        <span className={`text-[20px] sm:text-[22px] md:text-[18px] lg:text-[20px] font-bold mt-1 md:mt-2 text-[#8B6B5A] ${KAITI_FONT}`}>
          （<span className={getRatingColor(rating)}>{rating}</span>·{wuxing}）
        </span>
      </div>
    </>
  );
};

/** 六格详情卡片（严格模仿参考设计） */
const AnalysisCard: React.FC<{
  data: GridResult;
  headerText: string;
  subHeaderText?: string;
}> = ({ data, headerText, subHeaderText }) => {
  const displayRating = getGridAnalysisBadgeText(data);
  const ratingLines = splitBadgeLines(displayRating);
  const isBadgeMultiLine = ratingLines.length > 1;
  const ratingCharLen = Array.from(String(displayRating || '').replace(/\s+/g, '')).length;
  const ratingBg = getRatingBg(displayRating);
  const ratingSizeClass = isBadgeMultiLine
    ? 'h-12 w-12 sm:h-14 sm:w-14'
    : ratingCharLen <= 1
      ? 'h-9 w-9 sm:h-10 sm:w-10'
      : 'h-11 w-11 sm:h-12 sm:w-12';
  const ratingTextClass = isBadgeMultiLine
    ? 'text-[9px] sm:text-[10px] leading-[1.15]'
    : ratingCharLen <= 1
      ? 'text-[12px] sm:text-[13px]'
      : ratingCharLen <= 4
        ? 'text-[10px] sm:text-[11px] leading-tight'
        : 'text-[9px] sm:text-[10px] leading-tight';
  const details = parseFullTextToDetails(data.fullText81).filter((detail) => detail.label !== '寓意');
  const tagColor = (label: string) => {
    if (label === '基业') return 'bg-[#B06A6A]';
    if (label === '家庭') return 'bg-[#507C8C]';
    if (label === '健康') return 'bg-[#7E9A68]';
    if (label === '含义' || label === '整体') return 'bg-[#8E6E56]';
    return 'bg-[#C87635]';
  };
  return (
    <div className="mb-5 w-full">
      <div className="flex w-full flex-row overflow-hidden rounded-xl border border-[#E8DFD1] bg-white shadow-[0_4px_14px_rgba(148,87,67,0.08)]">
        <div className="relative flex w-10 shrink-0 items-center justify-center bg-[#945743] py-2.5 text-white sm:w-12 sm:py-3">
          <span
            className={`text-base font-bold leading-none tracking-[0.12em] sm:text-lg ${KAITI_FONT}`}
            style={{ writingMode: 'vertical-rl', textOrientation: 'upright' }}
          >
            {data.label}
          </span>
        </div>
        <div className="flex flex-1 flex-col">
          <div className="flex items-start justify-between gap-2.5 bg-[#FDFBF7] px-3 py-2.5 sm:px-4 sm:py-3">
            <div className="pr-1 text-[13px] font-bold leading-[1.45] tracking-[0.01em] text-[#4A3C31] font-sans sm:text-[14px]">
              {headerText}
            </div>
            <div className={`mt-0.5 flex shrink-0 items-center justify-center rounded-full px-1 text-center font-bold text-white shadow-sm ${ratingSizeClass} ${ratingTextClass} ${ratingBg} ${KAITI_FONT}`}>
              {isBadgeMultiLine ? (
                <span className="flex flex-col items-center justify-center">
                  {ratingLines.map((line, lineIdx) => (
                    <span key={`${data.key}-rating-line-${lineIdx}`}>{line}</span>
                  ))}
                </span>
              ) : (
                displayRating
              )}
            </div>
          </div>
          {subHeaderText && (
            <div className="border-t border-[#F0E5D8] bg-[#FAF6F0] px-3 py-1.5 text-[12px] leading-[1.5] text-[#9B8876] font-sans sm:px-4 sm:py-2">
              {subHeaderText}
            </div>
          )}
        </div>
      </div>
      <div className="mt-2.5 space-y-1.5 px-0.5 sm:px-1">
        {details.map((detail, idx) => {
          const displayLabel = detail.label === '含义' || detail.label === '整体' ? '整体' : detail.label;
          return (
            <div key={idx} className="flex items-start px-1 py-0.5 text-[13px] sm:text-[14px]">
              <div className="mt-0.5 shrink-0">
                <span
                  className={`mr-2.5 inline-flex min-h-[20px] items-center justify-center whitespace-nowrap rounded-sm px-2 py-0.5 text-[11px] font-bold leading-none tracking-[0.12em] text-white sm:text-[12px] ${tagColor(detail.label)} ${KAITI_FONT}`}
                >
                  {displayLabel}
                </span>
              </div>
              <span className="flex-1 text-[#5A4535] font-sans leading-6">
                {splitDetailParagraphs(detail.value).map((paragraph, paragraphIdx) => (
                  <p key={`${idx}-paragraph-${paragraphIdx}`} className={paragraphIdx > 0 ? 'mt-1.5' : ''}>
                    {paragraph}
                  </p>
                ))}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface FrameDiagramPayload {
  template: FrameTemplate;
  cells: Record<FrameSlotKey, FrameCellData>;
  grids: Record<FrameGridKey, FrameGridData>;
}

const toFrameGridData = (grid: GridResult): FrameGridData => ({
  value: String(grid.value81),
  rating: getGridChartLuckText(grid),
  wuxing: grid.wuxing,
});

const buildFramePayloadFromResult = (result: SancaiLiugeResult): FrameDiagramPayload => {
  const surnameSlots = result.strokeSlots
    .filter((slot) => slot.part === 'surname')
    .sort((a, b) => a.index - b.index);
  const givenSlots = result.strokeSlots
    .filter((slot) => slot.part === 'given')
    .sort((a, b) => a.index - b.index);

  const surnameCount = (surnameSlots.length === 2 ? 2 : 1) as 1 | 2;
  const givenCount = (givenSlots.length === 2 ? 2 : 1) as 1 | 2;
  const template = FRAME_TEMPLATES[getFrameLayout(surnameCount, givenCount)];
  const cells = createDefaultFrameCells();

  if (surnameSlots[0]) cells.s1 = { kind: 'real', char: surnameSlots[0].char, stroke: surnameSlots[0].finalStroke };
  if (surnameSlots[1]) cells.s2 = { kind: 'real', char: surnameSlots[1].char, stroke: surnameSlots[1].finalStroke };
  if (givenSlots[0]) cells.g1 = { kind: 'real', char: givenSlots[0].char, stroke: givenSlots[0].finalStroke };
  if (givenSlots[1]) cells.g2 = { kind: 'real', char: givenSlots[1].char, stroke: givenSlots[1].finalStroke };

  return {
    template,
    cells,
    grids: {
      tiange: toFrameGridData(result.grids.tiange),
      renge: toFrameGridData(result.grids.renge),
      dige: toFrameGridData(result.grids.dige),
      biange: toFrameGridData(result.grids.biange),
      waige: toFrameGridData(result.grids.waige),
      zongge: toFrameGridData(result.grids.zongge),
    },
  };
};

const buildPreviewFramePayload = (layout: NameFrameLayout): FrameDiagramPayload => {
  const template = FRAME_TEMPLATES[layout];
  const cells = createDefaultFrameCells();

  if (template.surnameCount === 1) {
    cells.s1 = { kind: 'real', char: '王', stroke: 4 };
  } else {
    cells.s1 = { kind: 'real', char: '欧', stroke: 15 };
    cells.s2 = { kind: 'real', char: '阳', stroke: 17 };
  }

  if (template.givenCount === 1) {
    cells.g1 = { kind: 'real', char: '明', stroke: 8 };
  } else {
    cells.g1 = { kind: 'real', char: '小', stroke: 3 };
    cells.g2 = { kind: 'real', char: '明', stroke: 8 };
  }

  const s1 = cells.s1.stroke;
  const s2 = cells.s2.stroke || s1;
  const g1 = cells.g1.stroke;
  const g2 = cells.g2.stroke || g1;

  const tiange = template.surnameCount === 1 ? s1 + 1 : s1 + s2;
  const renge = (template.surnameCount === 1 ? s1 : s2) + g1;
  const dige = template.givenCount === 1 ? g1 + 1 : g1 + g2;
  const biange = (template.surnameCount === 1 ? s1 : s2) + g2;
  const waige =
    template.surnameCount === 1 && template.givenCount === 1
      ? 2
      : template.surnameCount === 1
        ? 1 + g2
        : template.givenCount === 1
          ? s1 + 1
          : s1 + g2;
  const zongge = (template.surnameCount === 1 ? s1 : s1 + s2) + (template.givenCount === 1 ? g1 : g1 + g2);

  const asDemoGrid = (value: number): FrameGridData => ({
    value: String(value),
    rating: '平',
    wuxing: '示意',
  });

  return {
    template,
    cells,
    grids: {
      tiange: asDemoGrid(tiange),
      renge: asDemoGrid(renge),
      dige: asDemoGrid(dige),
      biange: asDemoGrid(biange),
      waige: asDemoGrid(waige),
      zongge: asDemoGrid(zongge),
    },
  };
};

/** 将 FrameDiagramPayload 转为 slots 数组 */
const payloadToSlots = (payload: FrameDiagramPayload): { char: string; stroke: string; isDummy: boolean }[] => {
  const { template, cells } = payload;
  return template.slotOrder.map((slotKey) => {
    const cell = cells[slotKey];
    const isDummy = cell.kind === 'virtual';
    return {
      char: isDummy ? '1' : cell.char || '',
      stroke: String(cell.stroke > 0 ? cell.stroke : 1),
      isDummy,
    };
  });
};

const NameFrameDiagram: React.FC<{ payload: FrameDiagramPayload }> = ({ payload }) => {
  const { template, grids } = payload;
  const slots = payloadToSlots(payload);
  const r0 = 12;
  const r1 = 32;
  const r2 = 52;
  const r3 = 72;
  const gap = 2;
  const waiBottom = template.givenCount === 1 ? r3 : r3 + gap;
  const isSingleName = template.givenCount === 1;

  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const BASE_WIDTH = 760;
  const BASE_HEIGHT = 800;

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const currentWidth = containerRef.current.offsetWidth;
        setScale(currentWidth < BASE_WIDTH ? currentWidth / BASE_WIDTH : 1);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
  <div className="w-full max-w-full overflow-hidden rounded-2xl border border-[#E8DCCF] bg-gradient-to-b from-[#FFFDF9] via-[#FFF9F0] to-[#FFFDF9] shadow-[0_10px_22px_rgba(148,87,67,0.08)] lg:mx-auto lg:max-w-[680px] lg:px-6">
      <div ref={containerRef} className="relative mx-auto w-full overflow-visible lg:max-w-[560px]" style={{ height: `${BASE_HEIGHT * scale}px` }}>
        <div
          className="absolute top-0 left-1/2"
          style={{
            width: `${BASE_WIDTH}px`,
            height: `${BASE_HEIGHT}px`,
            transform: `translateX(-50%) scale(${scale})`,
            transformOrigin: 'top center',
          }}
        >
          <SlotNode top={r0} charData={slots[0]!} />
          <SlotNode top={r1} charData={slots[1]!} />
          <SlotNode top={r2} charData={slots[2]!} />
          <SlotNode top={r3} charData={slots[3]!} />

          <RightBracket top={r0} bottom={r1 - gap} topStart={57} bottomStart={57} vLineRight={20} title="天格" value={grids.tiange.value} rating={grids.tiange.rating} wuxing={grids.tiange.wuxing} />
          <RightBracket top={r1 + gap} bottom={r2 - gap} topStart={57} bottomStart={57} vLineRight={20} title="人格" value={grids.renge.value} rating={grids.renge.rating} wuxing={grids.renge.wuxing} />
          <RightBracket top={r2 + gap} bottom={r3} topStart={57} bottomStart={57} vLineRight={20} title="地格" value={grids.dige.value} rating={grids.dige.rating} wuxing={grids.dige.wuxing} />

          <LeftBracket top={r0} bottom={waiBottom} topStart={40} bottomStart={40} vLineLeft={18} title="外格" value={grids.waige.value} rating={grids.waige.rating} wuxing={grids.waige.wuxing} />
          <LeftBracket top={r1} bottom={isSingleName ? r2 : r3 - gap} topStart={40} bottomStart={40} vLineLeft={37} textGap={3} title="变格" value={grids.biange.value} rating={grids.biange.rating} wuxing={grids.biange.wuxing} />

          <div className="absolute z-10 border-t-2 border-[#DBCAB8]" style={{ top: '84%', left: '12%', right: '12%' }} />
          <div className="absolute z-20 flex flex-col items-center justify-center whitespace-nowrap" style={{ top: '92%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            <div className="flex items-center gap-2 md:gap-3">
              <span className={`text-[26px] sm:text-[28px] md:text-[24px] lg:text-[26px] font-bold text-[#4A3C31] ${KAITI_FONT}`}>总格</span>
              <span className={`text-[32px] sm:text-[34px] md:text-[28px] lg:text-[30px] font-bold text-[#8B7D72] ${SERIF_NUM_FONT}`}>{grids.zongge.value}</span>
            </div>
            <span className={`text-[20px] sm:text-[22px] md:text-[18px] lg:text-[20px] font-bold mt-1 md:mt-2 text-[#8B6B5A] ${KAITI_FONT}`}>
              （<span className={getRatingColor(grids.zongge.rating)}>{grids.zongge.rating}</span>·{grids.zongge.wuxing}）
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const SancaiLiuge: React.FC<SancaiLiugeProps> = ({
  onBack,
  currentUser = null,
  onOpenKangxiDictionary,
  initialSurname = '',
  initialGivenName = '',
  pendingRecordId = null,
  onPendingRecordConsumed,
}) => {
  const [view, setView] = useState<ViewMode>('input');
  const [showRenameConsult, setShowRenameConsult] = useState(false);
  const [surname, setSurname] = useState('');
  const [givenName, setGivenName] = useState('');
  const [ageInput, setAgeInput] = useState('');
  const [authUser, setAuthUser] = useState<StoredUser | null>(() => currentUser || getStoredUser());
  const [showProUnlockModal, setShowProUnlockModal] = useState(false);
  const [proUnlockCode, setProUnlockCode] = useState('');
  const [proUnlockLoading, setProUnlockLoading] = useState(false);
  const [proUnlockNotice, setProUnlockNotice] = useState('');
  const [pendingLiveOpen, setPendingLiveOpen] = useState(false);
  const [proReportMode, setProReportMode] = useState<ProfessionalNameStudyMode>('demo');
  const [proReportData, setProReportData] = useState<ProfessionalNameStudyReport | null>(null);
  const [proReportError, setProReportError] = useState('');
  const [proReportLoading, setProReportLoading] = useState(false);
  const [proReportCacheKey, setProReportCacheKey] = useState('');
  const [proReportProgressPercent, setProReportProgressPercent] = useState(0);
  const [proReportProgressIndex, setProReportProgressIndex] = useState(0);

  const resolveLatestAuthUser = useCallback((): StoredUser | null => {
    const latestUser = getStoredUser();
    const resolved = currentUser || latestUser || authUser || null;
    if (resolved !== authUser) {
      setAuthUser(resolved);
    }
    return resolved;
  }, [authUser, currentUser]);

  useEffect(() => {
    if (!currentUser?.userId) return;
    setAuthUser(currentUser);
  }, [currentUser]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlSurname = params.get('surname') || '';
    const urlGivenName = params.get('givenName') || '';
    const urlGender = params.get('gender') === 'female' ? 'female' : params.get('gender') === 'male' ? 'male' : '';
    const s = initialSurname || urlSurname;
    const g = initialGivenName || urlGivenName;
    if (s || g) {
      setSurname(s);
      setGivenName(g);
    }
    if (urlGender) {
      setGender(urlGender);
    }
  }, [initialSurname, initialGivenName]);
  const [gender, setGender] = useState<SancaiGender>('male');
  const [errors, setErrors] = useState<string[]>([]);
  const [records, setRecords] = useState<StoredRecord[]>(() => loadRecords());
  const [result, setResult] = useState<SancaiLiugeResult | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [isResolvingStrokes, setIsResolvingStrokes] = useState(false);
  const [onlineStrokeMap, setOnlineStrokeMap] = useState<Record<string, number>>(() => loadOnlineStrokeCache());
  const [reportQrDataUrl, setReportQrDataUrl] = useState('');
  const resultRef = useRef<HTMLDivElement>(null);
  const proReportRef = useRef<HTMLDivElement>(null);
  const proExportRef = useRef<HTMLDivElement>(null);
  const initialPageHandledRef = useRef(false);
  const recordsRef = useRef<StoredRecord[]>(loadRecords());
  const cloudRecordsReadyRef = useRef(false);
  const cloudRecordsSyncHashRef = useRef('');
  const proProgressTimerRef = useRef<number | null>(null);

  const ageValue = useMemo(() => parseAgeValue(ageInput), [ageInput]);
  const hasProfessionalAccess = useMemo(
    () => isAdminMember(authUser) || hasContentUnlock(PROFESSIONAL_NAME_STUDY_UNLOCK_ID, authUser),
    [authUser],
  );

  const stopProProgress = () => {
    if (proProgressTimerRef.current != null) {
      window.clearInterval(proProgressTimerRef.current);
      proProgressTimerRef.current = null;
    }
  };

  const startProProgress = () => {
    stopProProgress();
    setProReportProgressPercent(0);
    setProReportProgressIndex(0);
    const startedAt = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      const ratio = Math.min(1, elapsed / PROFESSIONAL_NAME_STUDY_PROGRESS_DURATION_MS);
      // 匀速进度 - 不使用缓动函数
      const next = ratio * 95;
      const stepSize = 100 / PROFESSIONAL_NAME_STUDY_PROGRESS_STEPS.length;
      const nextIndex = Math.min(
        PROFESSIONAL_NAME_STUDY_PROGRESS_STEPS.length - 1,
        Math.max(0, Math.floor(next / stepSize)),
      );
      setProReportProgressPercent(next);
      setProReportProgressIndex(nextIndex);
    };
    tick();
    proProgressTimerRef.current = window.setInterval(tick, PROFESSIONAL_NAME_STUDY_PROGRESS_TICK_MS);
  };

  const sleep = useCallback((ms: number) => new Promise<void>((resolve) => {
    window.setTimeout(resolve, Math.max(0, ms));
  }), []);


  const applyStoredRecords = (nextRecords: StoredRecord[]): StoredRecord[] => {
    const normalized = mergeStoredRecordsById(nextRecords).slice(0, 60);
    recordsRef.current = normalized;
    setRecords(normalized);
    return normalized;
  };

  const strokeSlots = useMemo(
    () => buildStrokeSlots(surname, givenName, onlineStrokeMap),
    [surname, givenName, onlineStrokeMap],
  );
  const quickLookupChars = useMemo(
    () => Array.from(new Set([...sanitizeName(surname), ...sanitizeName(givenName)])),
    [surname, givenName],
  );
  const demoResult = useMemo(
    () => calculateSancaiLiuge({ surname: '张', givenName: '一二', gender: 'male' }).result,
    [],
  );
  const activeProResult = useMemo(
    () => (proReportMode === 'live' && result ? result : demoResult),
    [demoResult, proReportMode, result],
  );
  const activeProAge = proReportMode === 'live' ? ageValue : PROFESSIONAL_NAME_STUDY_DEMO_AGE;

  useEffect(() => {
    const syncUser = () => setAuthUser(getStoredUser());
    syncUser();
    window.addEventListener('storage', syncUser);
    return () => window.removeEventListener('storage', syncUser);
  }, []);

  useEffect(() => {
    recordsRef.current = records;
    saveRecords(records);
  }, [records]);

  useEffect(() => {
    let cancelled = false;
    const hydrateCloudRecords = async () => {
      const remote = await fetchUserRecordCollection<StoredRecord>('sancai');
      if (cancelled) return;
      if (!remote) {
        cloudRecordsReadyRef.current = false;
        cloudRecordsSyncHashRef.current = JSON.stringify(recordsRef.current);
        return;
      }
      const merged = mergeStoredRecordsById([
        ...recordsRef.current,
        ...(Array.isArray(remote.records) ? remote.records : []),
      ]).slice(0, 60);
      recordsRef.current = merged;
      setRecords(merged);
      cloudRecordsReadyRef.current = true;
      cloudRecordsSyncHashRef.current = JSON.stringify(merged);
    };
    void hydrateCloudRecords();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!cloudRecordsReadyRef.current) return;
    const nextHash = JSON.stringify(records);
    if (nextHash === cloudRecordsSyncHashRef.current) return;
    const timer = window.setTimeout(() => {
      void saveUserRecordCollection('sancai', records).then((ok) => {
        if (ok) {
          cloudRecordsSyncHashRef.current = nextHash;
        }
      });
    }, 450);
    return () => window.clearTimeout(timer);
  }, [records]);

  useEffect(() => () => stopProProgress(), []);

  const handleSubmit = async () => {
    const cleanSurname = sanitizeName(surname);
    const cleanGivenName = sanitizeName(givenName);
    if (cleanSurname !== surname) setSurname(cleanSurname);
    if (cleanGivenName !== givenName) setGivenName(cleanGivenName);

    let activeOnlineMap = onlineStrokeMap;
    let onlineLookupFailed = false;
    const preSlots = buildStrokeSlots(cleanSurname, cleanGivenName, activeOnlineMap);
    const missingChars = Array.from(
      new Set(
        preSlots
          .filter((slot) => slot.finalStroke <= 0)
          .flatMap((slot) => [slot.char, slot.normalizedChar].filter(Boolean)),
      ),
    );

    if (missingChars.length > 0) {
      setIsResolvingStrokes(true);
      try {
        const fetchedMap = await lookupKangxiStrokesApi(missingChars);
        if (Object.keys(fetchedMap).length > 0) {
          activeOnlineMap = { ...activeOnlineMap, ...fetchedMap };
          setOnlineStrokeMap(activeOnlineMap);
          saveOnlineStrokeCache(activeOnlineMap);
        }
      } catch {
        onlineLookupFailed = true;
      } finally {
        setIsResolvingStrokes(false);
      }
    }

    const unresolved = buildStrokeSlots(cleanSurname, cleanGivenName, activeOnlineMap)
      .filter((item) => !Number.isFinite(item.finalStroke) || item.finalStroke <= 0);
    if (unresolved.length > 0) {
      const chars = unresolved.map((item) => item.char).join('、');
      if (onlineLookupFailed) {
        setErrors([`当前网络不可用，且以下字符未能在本地字库识别：${chars}。请联网后重试。`]);
      } else {
        setErrors([`以下字符暂未查到有效姓名学笔画：${chars}。请稍后再试。`]);
      }
      return;
    }

    const calc = calculateSancaiLiuge({
      surname: cleanSurname,
      givenName: cleanGivenName,
      gender,
      onlineStrokes: activeOnlineMap,
    });

    if (!calc.result) {
      setErrors(calc.errors);
      return;
    }

    setErrors([]);
    setResult(calc.result);
    setView('result');
    setShowRenameConsult(false);
    setProReportMode('live');

    const nextRecord: StoredRecord = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      surname: calc.result.surname,
      givenName: calc.result.givenName,
      gender: calc.result.gender,
      age: ageValue,
      result: calc.result,
    };

    applyStoredRecords([nextRecord, ...recordsRef.current]);
  };

  const handleReset = () => {
    setView('input');
    setErrors([]);
  };

  const openRecord = (record: StoredRecord) => {
    applyStoredRecords([{ ...record, updatedAt: Date.now() }, ...recordsRef.current.filter((item) => item.id !== record.id)]);
    setSurname(record.surname);
    setGivenName(record.givenName);
    setGender(record.gender);
    setAgeInput(record.age ? String(record.age) : '');
    setResult(record.result);
    setErrors([]);
    setShowRenameConsult(false);
    setView('result');
  };

  useLayoutEffect(() => {
    const targetId = String(pendingRecordId || '').trim();
    if (!targetId) return;
    const target = records.find((item) => String(item.id) === targetId);
    if (target) {
      openRecord(target);
    }
    onPendingRecordConsumed?.(targetId);
  }, [pendingRecordId, records, onPendingRecordConsumed]);

  const removeRecord = (id: string) => {
    applyStoredRecords(recordsRef.current.filter((item) => item.id !== id));
  };

  const clearRecords = () => {
    applyStoredRecords([]);
  };

  const openProfessionalUnlock = () => {
    setPendingLiveOpen(true);
    setProUnlockNotice('');
    setShowProUnlockModal(true);
  };

  const closeProfessionalUnlock = () => {
    setPendingLiveOpen(false);
    setShowProUnlockModal(false);
    setProUnlockNotice('');
  };

  const copyTeacherWechat = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(PRO_TEACHER_WECHAT);
      } else {
        const temp = document.createElement('input');
        temp.value = PRO_TEACHER_WECHAT;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
      }
      setProUnlockNotice('微信号已复制');
    } catch {
      setProUnlockNotice(`请手动添加若轩老师微信 ${PRO_TEACHER_WECHAT}`);
    }
  };

  const openSampleProReport = () => {
    setShowRenameConsult(false);
    setProReportMode('demo');
    setView('proReport');
  };

  const handleOpenProReport = () => {
    if (!result) {
      openSampleProReport();
      return;
    }
    const latestUser = resolveLatestAuthUser();
    const canAccess = isAdminMember(latestUser) || hasContentUnlock(PROFESSIONAL_NAME_STUDY_UNLOCK_ID, latestUser);
    if (!canAccess) {
      openProfessionalUnlock();
      return;
    }
    setPendingLiveOpen(false);
    setShowRenameConsult(false);
    setProReportMode('live');
    setView('proReport');
  };

  const handleOpenProExport = () => {
    setView('proExport');
  };

  const handleProUnlockSubmit = async () => {
    const latestUser = resolveLatestAuthUser();
    const codeText = String(proUnlockCode || '').trim();
    if (!latestUser?.userId) {
      setProUnlockNotice('请先登录后再输入激活码。');
      return;
    }
    if (!codeText) {
      setProUnlockNotice('请输入激活码。');
      return;
    }
    setProUnlockLoading(true);
    setProUnlockNotice('');
    try {
      const updated = await activateMembershipCode(codeText);
      saveStoredUser(updated);
      setAuthUser(updated);
      setProUnlockCode('');
      const unlocked = isAdminMember(updated) || hasContentUnlock(PROFESSIONAL_NAME_STUDY_UNLOCK_ID, updated);
      if (!unlocked) {
        setProUnlockNotice('激活成功，但当前激活码未开通专业姓名学报告权限，请联系若轩老师确认激活码类型。');
        return;
      }
      closeProfessionalUnlock();
      if (pendingLiveOpen) {
        setShowRenameConsult(false);
        setProReportMode('live');
        setView('proReport');
      }
    } catch (error) {
      setProUnlockNotice(error instanceof Error ? error.message : '激活失败，请稍后再试。');
    } finally {
      setProUnlockLoading(false);
    }
  };

  useEffect(() => {
    if (initialPageHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const page = params.get('page');
    if (page !== 'proReport') return;
    initialPageHandledRef.current = true;
    if (params.get('demo') === '1' || !result) {
      openSampleProReport();
      return;
    }
    handleOpenProReport();
  }, [result, hasProfessionalAccess]);

  const getSancaiLiugeUrl = (page?: 'proReport', mode: ProfessionalNameStudyMode = 'live'): string => {
    const params = new URLSearchParams();
    params.set('tool', 'sancaiLiuge');
    if (page) params.set('page', page);
    if (page === 'proReport' && mode === 'demo') params.set('demo', '1');
    return `${window.location.origin}${window.location.pathname || '/home'}?${params.toString()}`;
  };

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(getSancaiLiugeUrl(), { width: 240, margin: 1 })
      .then((url) => {
        if (!cancelled) setReportQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setReportQrDataUrl('');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (view !== 'proReport' || !activeProResult) return;
    if (proReportMode === 'demo') {
      setProReportError('');
      setProReportData(PROFESSIONAL_NAME_STUDY_DEMO_REPORT);
      setProReportCacheKey(buildProfessionalNameStudyCacheKey(activeProResult, activeProAge, proReportMode));
      setProReportLoading(false);
      stopProProgress();
      setProReportProgressPercent(100);
      setProReportProgressIndex(PROFESSIONAL_NAME_STUDY_PROGRESS_STEPS.length - 1);
      return;
    }
    const cacheKey = buildProfessionalNameStudyCacheKey(activeProResult, activeProAge, proReportMode);
    if (cacheKey === proReportCacheKey && proReportData) return;

    let cancelled = false;
    const fallback = buildProfessionalNameStudyFallback(activeProResult, activeProAge);

    const run = async () => {
      setProReportLoading(true);
      setProReportError('');
      setProReportData(null);
      startProProgress();
      try {
        let responseText = '';
        let lastError: unknown = null;
        for (let attempt = 0; attempt < PROFESSIONAL_NAME_STUDY_MAX_RETRIES; attempt += 1) {
          try {
            const response = await chatApi({
              feature: PROFESSIONAL_NAME_STUDY_FEATURE_ID,
              featureMode: proReportMode === 'demo' ? 'sample' : 'live',
              timeoutMs: 120000,
              messages: [
                {
                  role: 'user',
                  content: buildProfessionalNameStudyUserMessage(activeProResult, activeProAge, proReportMode),
                },
              ],
              usageQuestion: `${activeProResult.fullName}专业姓名学报告`,
            });
            responseText = String(response.text || '').trim();
            if (responseText) break;
            lastError = new Error('AI 未返回可用内容');
          } catch (error) {
            lastError = error;
          }
          if (attempt < PROFESSIONAL_NAME_STUDY_MAX_RETRIES - 1) {
            setProReportError(`AI 生成波动，正在自动重试（${attempt + 1}/${PROFESSIONAL_NAME_STUDY_MAX_RETRIES - 1}）…`);
            await sleep(PROFESSIONAL_NAME_STUDY_RETRY_DELAY_MS * (attempt + 1));
          }
        }
        if (!responseText) {
          throw (lastError instanceof Error ? lastError : new Error('专业报告生成失败，请稍后重试。'));
        }
        if (cancelled) return;
        setProReportData(parseProfessionalNameStudyReport(responseText, fallback));
        setProReportCacheKey(cacheKey);
      } catch (error) {
        if (cancelled) return;
        setProReportData(fallback);
        setProReportCacheKey(cacheKey);
        setProReportError(`AI 生成异常，已自动重试，当前展示为结构化备用结果。${error instanceof Error ? error.message : '请稍后重试。'}`);
      } finally {
        if (cancelled) return;
        stopProProgress();
        setProReportProgressPercent(100);
        setProReportProgressIndex(PROFESSIONAL_NAME_STUDY_PROGRESS_STEPS.length - 1);
        window.setTimeout(() => {
          if (!cancelled) setProReportLoading(false);
        }, 260);
      }
    };

    void run();
    return () => {
      cancelled = true;
      stopProProgress();
    };
  }, [activeProAge, activeProResult, proReportMode, view]);

  const renderCanvasFromRef = async (target: HTMLDivElement): Promise<HTMLCanvasElement> => {
    try {
      const snapshot = await snapdom(target, {
        scale: 2,
        backgroundColor: '#FAF8F2',
        embedFonts: true,
        exclude: ['[data-export-hide]'],
        excludeMode: 'remove',
      });
      return await snapshot.toCanvas();
    } catch (error) {
      console.warn('snapdom export failed, fallback to html2canvas', error);
      return await html2canvas(target, {
        backgroundColor: '#FAF8F2',
        scale: Math.min(window.devicePixelRatio || 2, 2),
        useCORS: true,
        logging: false,
        ignoreElements: (element) => element instanceof HTMLElement && element.dataset.exportHide !== undefined,
      });
    }
  };

  const exportImage = async (targetRef: React.RefObject<HTMLDivElement>, fileNamePrefix = '三才六格') => {
    if (!targetRef.current) return;
    setIsExporting(true);
    setExportError('');
    try {
      await document.fonts.ready;
      const out = await renderCanvasFromRef(targetRef.current);
      const targetName = result?.fullName || demoResult?.fullName || '报告';
      const fileName = `${fileNamePrefix}_${targetName}.png`;
      const triggerDownload = (href: string) => {
        const link = document.createElement('a');
        link.href = href;
        link.download = fileName;
        link.rel = 'noopener';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };
      const blob = await new Promise<Blob | null>((resolve) => out.toBlob((b) => resolve(b), 'image/png'));
      if (blob) {
        const blobUrl = URL.createObjectURL(blob);
        triggerDownload(blobUrl);
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1800);
      } else {
        triggerDownload(out.toDataURL('image/png'));
      }
    } catch (error) {
      console.error('sancai report export failed', error);
      setExportError('下载失败，请重试；若仍失败可长按图片截图保存。');
    } finally {
      setIsExporting(false);
    }
  };

  const renderInput = () => (
    <div className="min-h-screen bg-[#ebe8e1] text-slate-900">
      <div className="bg-gradient-to-b from-[#c79a61] via-[#b88a52] to-[#ad7f48] border-b border-[#d9ccb7]/95 pb-20">
        <XuanYuanTopBar
          title="轩辕国学·三才六格"
          onBack={onBack}
          variant="goldOverlay"
          sticky={false}
          maxWidthClassName="max-w-2xl"
        />
      </div>
      <div className="-mt-16 max-w-2xl mx-auto px-4 pb-20 pt-2 sm:pb-20">
      <div className="min-h-[56vh] flex flex-col justify-center sm:min-h-0 sm:block">

      <div className="bg-white rounded-2xl border border-[#e7d8c1] shadow-[0_14px_30px_rgba(98,67,34,0.18)] overflow-hidden">
        <div className="flex items-center border-b border-slate-200 px-4 sm:px-5 py-3 sm:py-3.5">
          <label className="w-20 sm:w-24 text-base font-semibold text-slate-700 shrink-0">姓氏</label>
          <input
            value={surname}
            onChange={(e) => {
              const next = e.target.value;
              setSurname(next);
            }}
            onCompositionEnd={(e) => {
              setSurname(sanitizeName(e.currentTarget.value));
            }}
            onBlur={(e) => setSurname(sanitizeName(e.currentTarget.value))}
            placeholder="请填写您的姓氏"
            className="flex-1 text-lg leading-[1.35] text-slate-700 placeholder:text-slate-300 outline-none"
          />
        </div>

        <div className="flex items-center border-b border-slate-200 px-4 sm:px-5 py-3 sm:py-3.5">
          <label className="w-20 sm:w-24 text-base font-semibold text-slate-700 shrink-0">名字</label>
          <input
            value={givenName}
            onChange={(e) => {
              const next = e.target.value;
              setGivenName(next);
            }}
            onCompositionEnd={(e) => {
              setGivenName(sanitizeName(e.currentTarget.value));
            }}
            onBlur={(e) => setGivenName(sanitizeName(e.currentTarget.value))}
            placeholder="请填写您的名字"
            className="flex-1 text-lg leading-[1.35] text-slate-700 placeholder:text-slate-300 outline-none"
          />
        </div>

        <div className="px-4 sm:px-5 py-3 sm:py-3.5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <p className="text-base font-semibold text-slate-700 w-20 sm:w-24 shrink-0">性别</p>
            <button
              onClick={() => setGender('male')}
              className={`px-3 py-1.5 rounded-full border text-base font-semibold transition-colors ${
                gender === 'male'
                  ? 'bg-[#cfb173] text-white border-[#cfb173]'
                  : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50'
              }`}
            >
              男
            </button>
            <button
              onClick={() => setGender('female')}
              className={`px-3 py-1.5 rounded-full border text-base font-semibold transition-colors ${
                gender === 'female'
                  ? 'bg-[#cfb173] text-white border-[#cfb173]'
                  : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50'
              }`}
            >
              女
            </button>
          </div>
        </div>

        <div className="flex items-center border-b border-slate-200 px-4 sm:px-5 py-3 sm:py-3.5">
          <label className="w-20 sm:w-24 text-base font-semibold text-slate-700 shrink-0">年龄</label>
          <input
            value={ageInput}
            onChange={(e) => setAgeInput(normalizeAgeInput(e.target.value))}
            placeholder="选填，便于 AI 按年龄阶段精细解读"
            inputMode="numeric"
            className="flex-1 text-lg leading-[1.35] text-slate-700 placeholder:text-slate-300 outline-none"
          />
        </div>

        {onOpenKangxiDictionary && quickLookupChars.length > 0 && (
          <div className="px-4 sm:px-5 py-3 sm:py-3.5 border-b border-slate-200 bg-stone-50/70">
            <p className="text-xs text-slate-600">查字笔画（直接进入康熙字典）</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {quickLookupChars.map((char) => (
                <button
                  key={`quick-dict-${char}`}
                  onClick={() => onOpenKangxiDictionary(char)}
                  className="inline-flex items-center rounded-full border border-[#cdb78f] bg-white px-2.5 py-1 text-xs text-[#8b6b3d] hover:bg-[#f8f1e4]"
                >
                  {char} · 查字典
                </button>
              ))}
            </div>
          </div>
        )}

        {false && (
        <div className="px-3 sm:px-4 py-3 bg-stone-50">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-700">笔画识别（自动）</p>
            <p className="text-xs text-slate-500">本地姓名学字库 + 联网笔画自动补齐</p>
          </div>

          {strokeSlots.length === 0 ? (
            <p className="text-xs text-slate-400">先填写姓和名后显示笔画来源</p>
          ) : (
            <div className="space-y-2">
              {strokeSlots.map((slot) => (
                <div key={slot.key} className="grid grid-cols-[48px_1fr_72px] gap-1.5 items-center bg-white rounded-lg px-2.5 py-1.5 border border-slate-200">
                  <div className="text-center">
                    <p className="text-xs text-slate-500">{slot.partLabel}{slot.index + 1}</p>
                    <p className="text-lg font-serif font-bold text-slate-800">{slot.char}</p>
                  </div>

                  <div>
                    <p className="text-xs text-slate-600">
                      自动：
                      {slot.autoStroke > 0 ? (
                        <span className="font-semibold text-slate-800">{slot.autoStroke} 画</span>
                      ) : slot.source === 'online' && slot.finalStroke > 0 ? (
                        <span className="font-semibold text-emerald-700">联网补齐 {slot.finalStroke} 画</span>
                      ) : (
                        <span className="text-rose-600 font-semibold">未识别</span>
                      )}
                    </p>
                    {slot.char !== slot.normalizedChar && (
                      <p className="text-[11px] text-slate-400">按“{slot.normalizedChar}”计</p>
                    )}
                    <p className="text-[11px] text-slate-500">
                      来源：
                      {slot.source === 'online'
                        ? '联网笔画'
                        : slot.source === 'dict'
                          ? '本地字库'
                          : '待联网补齐'}
                    </p>
                  </div>

                  <div className="text-center">
                    <p className="text-xs text-slate-500">最终</p>
                    <p className="text-sm font-bold text-slate-800">{slot.finalStroke > 0 ? `${slot.finalStroke}画` : '--'}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

      </div>

      {errors.length > 0 && (
        <div className="mt-4 bg-rose-50 border border-rose-200 rounded-xl p-3 space-y-1">
          {errors.map((item) => (
            <p key={item} className="text-sm text-rose-700">
              {item}
            </p>
          ))}
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={isResolvingStrokes}
        className={`w-full mt-6 py-3 rounded-xl text-white text-base font-semibold transition-colors ${
          isResolvingStrokes ? 'bg-[#9b8462] cursor-not-allowed' : 'bg-[#8b6b3d] hover:bg-[#785a31]'
        }`}
      >
        {isResolvingStrokes ? '正在查询姓名学笔画...' : '开始解读'}
      </button>

      <button
        onClick={() => setView('records')}
        className="w-full mt-4 text-[#b69f6f] underline text-sm font-medium"
      >
        查看记录列表
      </button>
      </div>
    </div>
    </div>
  );

  const renderResult = () => {
    if (!result) return null;

    const grids = result.grids;
    const ordered = GRID_ORDER.map((key) => grids[key]);
    const framePayload = buildFramePayloadFromResult(result);
    const sortedSlots = [...result.strokeSlots].sort((a, b) => {
      if (a.part === b.part) return a.index - b.index;
      return a.part === 'surname' ? -1 : 1;
    });

    return (
      <div className="min-h-screen bg-[#FAF8F2]">
        <div className="flex justify-center px-4 py-4 sm:py-8">
        <div ref={resultRef} className="relative w-full max-w-[1100px] px-2 py-5 pb-20 sm:px-4">
          <button
            type="button"
            onClick={() => setView('input')}
            className="absolute left-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#DCC3A8] bg-white/90 text-[#7A4E31] shadow-[0_4px_12px_rgba(148,87,67,0.12)] transition-colors hover:bg-[#fff8ef]"
            aria-label="返回"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          {/* 姓名诊断标题：品牌名压在线上，标题在窄屏也完整显示 */}
          <div className="relative py-8 sm:py-10" style={{ minHeight: 111 }}>
          <div className="absolute inset-x-0 top-5 h-px bg-gradient-to-r from-transparent via-[#DBCAB8] to-transparent" />
          <div className="absolute inset-x-0 bottom-4 h-px bg-gradient-to-r from-transparent via-[#DBCAB8] to-transparent" />
          <div className={`absolute left-1/2 top-5 -translate-x-1/2 -translate-y-1/2 bg-[#FAF8F2] px-3 text-center text-[18px] sm:text-[20px] tracking-[0.14em] text-[#7A4E31] ${KAITI_FONT}`}>
            轩辕国学
          </div>
          <div className="relative mx-auto mt-4 w-fit max-w-full rounded-sm border-y border-[#DCC3A8] bg-gradient-to-r from-[#FAF5EE] via-[#FFFDF9] to-[#FAF5EE] px-4 py-4 shadow-[0_6px_16px_rgba(148,87,67,0.1)] sm:px-8 sm:py-5">
              <div className="flex items-start justify-center gap-2 sm:gap-4">
                <span className="text-[#C87635] text-base sm:text-lg shrink-0">✦</span>
                <h1 className={`max-w-full text-center text-[24px] sm:text-[34px] md:text-[42px] font-bold text-[#4A3C31] tracking-[0.04em] whitespace-normal break-all ${KAITI_FONT}`} style={{ lineHeight: 1.2 }}>
                  {result.fullName}姓名诊断
                </h1>
                <span className="text-[#C87635] text-base sm:text-lg shrink-0">✦</span>
              </div>
            </div>
          </div>

        <div className="mb-6">
          <p className="text-[15px] sm:text-[16px] text-[#5A4535] leading-6 font-sans">
            六格姓名学含天格、人格、地格、外格、变格、总格，按笔画五行可推算人生六运。
          </p>
          <p className="mt-1.5 text-[15px] sm:text-[16px] text-[#5A4535] leading-6 font-sans">
            你的姓名对应三才显示为<span className="font-semibold"> {result.threeTalents.sequence} </span>，
            天人格：{result.threeTalents.relationTR}，人格地格：{result.threeTalents.relationRD}。
          </p>

          <div className="mt-3 mb-6 flex w-full flex-col">
            <SectionTitle title="五格数理图" />
            <NameFrameDiagram payload={framePayload} />

            <div className={`relative w-full px-4 pb-6 pt-3 sm:px-10 ${KAITI_FONT}`}>
              <table className="w-full text-center text-[#6A4E36]">
                <thead>
                  <tr className="text-[12px] sm:text-[14px] text-[#8B6B5A]">
                    <th />
                    <th />
                    <th>简体</th>
                    <th>繁体</th>
                    <th>姓名学</th>
                    <th />
                    <th />
                    {onOpenKangxiDictionary && <th />}
                  </tr>
                  <tr className="text-[12px] sm:text-[14px] text-[#8B6B5A] border-b border-[#DBCAB8]">
                    <th className="pb-2 font-normal">字</th>
                    <th className="pb-2 font-normal">用字</th>
                    <th className="pb-2 font-normal">笔画</th>
                    <th className="pb-2 font-normal">笔画</th>
                    <th className="pb-2 font-normal">笔画</th>
                    <th className="pb-2 font-normal">五行</th>
                    <th className="pb-2 font-normal">角色</th>
                    {onOpenKangxiDictionary && <th className="pb-2 font-normal">字典</th>}
                  </tr>
                </thead>
                <tbody className="text-[16px] sm:text-[18px]">
                  {sortedSlots.map((slot) => (
                    <tr key={slot.key} className="border-b border-transparent">
                      <td className="py-2 text-[#C87635] font-bold">{slot.char}</td>
                      <td>{slot.normalizedChar}</td>
                      <td className={SERIF_NUM_FONT}>{slot.autoStroke > 0 ? slot.autoStroke : '—'}</td>
                      <td className={SERIF_NUM_FONT}>{slot.finalStroke > 0 ? slot.finalStroke : '—'}</td>
                      <td className={SERIF_NUM_FONT}>{slot.finalStroke > 0 ? slot.finalStroke : '—'}</td>
                      <td>{slot.charWuxing || '—'}</td>
                      <td>{slot.partLabel}{slot.index + 1}</td>
                      {onOpenKangxiDictionary && (
                        <td>
                          <button
                            onClick={() => onOpenKangxiDictionary(slot.char)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors"
                            title="查康熙字典"
                          >
                            <BookOpen className="w-3.5 h-3.5" />
                            字典
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <SectionTitle title="六格详细分析" />

        <div className="space-y-3">
          {ordered.map((grid, idx) => {
            const headerMap: Record<string, string> = {
              tiange: getSimpleGridHeader(grid),
              renge: getSimpleGridHeader(grid),
              dige: getSimpleGridHeader(grid),
              waige: getSimpleGridHeader(grid),
              biange: getSimpleGridHeader(grid),
              zongge: getSimpleGridHeader(grid),
            };
            const subMap: Record<string, string> = {
              tiange: '注：天格为姓氏，非后天努力可改变，其数理吉凶影响不大。',
              renge: '注：人格为姓名之核心，影响人一生命运之吉凶。',
              dige: '注：地格为前运，主管36岁之前的活动力。',
              waige: '注：外格为副运，可辅助人格，观察社交及人缘。',
              biange: '注：变格为副运，主要辅助总格，对晚年运势有一定影响。',
              zongge: '注：总格为主运，主管36岁之后的运势吉凶。',
            };
            return (
              <React.Fragment key={grid.key}>
                <AnalysisCard
                  data={grid}
                  headerText={headerMap[grid.key] || `${grid.label}（${grid.value81}·${grid.wuxing}）`}
                  subHeaderText={subMap[grid.key]}
                />
                {(idx === 2 || idx === 4) && <DiamondDivider />}
              </React.Fragment>
            );
          })}
        </div>

        <DiamondDivider />

        <div className="mb-5">
          <SectionTitle title="三才与八运" />
          <div className="mb-4 rounded-xl border border-[#E7D8C8] bg-gradient-to-r from-[#FFF9F1] via-[#FFFEFB] to-[#FFF9F1] px-4 py-3 sm:px-5">
            <p className="text-[15px] font-semibold text-[#5A4535] font-sans">三才：{result.threeTalents.sequence}</p>
            <p className="mt-1 text-sm text-[#8A7C6E] font-sans">
              天人格：{result.threeTalents.relationTR} ｜ 人地格：{result.threeTalents.relationRD}
            </p>
            <p className="mt-1.5 text-[14px] sm:text-[15px] leading-6 text-[#5A4535] font-sans">{result.threeTalents.summary}</p>
          </div>

          <div className="space-y-2.5">
            {result.stages.map((stage) => (
              <div
                key={stage.key}
                className="rounded-xl border border-[#E8DFD1] bg-white shadow-[0_4px_12px_rgba(148,87,67,0.06)] overflow-hidden"
              >
                <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 sm:gap-3 bg-[#FDFBF7] border-b border-[#F0E5D8]">
                  <span className={`text-base sm:text-lg font-bold text-[#4A3C31] ${KAITI_FONT}`}>{stage.label}</span>
                  <span className="text-sm text-[#8A7C6E] font-sans">{stage.ageRange}</span>
                  <span className={`ml-auto inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${stageStyle(stage.level)}`}>
                    {stage.level}
                  </span>
                </div>
                <div className="px-4 py-2.5">
                  <p className="mb-1.5 text-[13px] text-[#8A7C6E] font-sans">依据：{stage.factors}</p>
                  <p className="text-[14px] sm:text-[15px] leading-6 text-[#5A4535] font-sans">{stage.summary}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-5">
          <SectionTitle title="健康相克提示" />
          {result.diseaseRisks.length === 0 ? (
            <div className="rounded-xl border border-[#D6E5D2] bg-gradient-to-r from-[#F7FBF3] via-[#FCFFFB] to-[#F7FBF3] px-4 py-2.5">
              <p className="text-sm leading-6 text-[#4E6643] font-sans">未发现人格与天格/地格/外格/变格形成明显五行相克。</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {result.diseaseRisks.map((risk) => (
                <div key={risk.rule} className="rounded-xl border border-rose-200 bg-gradient-to-r from-rose-50 via-white to-rose-50 px-4 py-2.5 shadow-[0_4px_12px_rgba(194,75,75,0.08)]">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-rose-700 font-sans">{risk.rule}</p>
                    <span className="inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">五行相克</span>
                  </div>
                  <p className="mt-0.5 text-sm text-rose-700 font-sans">易受影响：{risk.organs}</p>
                  <p className="mt-0.5 text-xs text-rose-600 font-sans">触发格位：{risk.triggeredBy.join('、')}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mb-6 rounded-[28px] border border-[#e7d2b6] bg-[radial-gradient(circle_at_top_right,_rgba(255,234,188,0.9),_rgba(255,249,238,0.98)_36%,_rgba(247,240,228,0.98)_100%)] px-5 py-5 shadow-[0_18px_40px_rgba(122,74,17,0.12)]">
          <p className="text-left text-[15px] leading-7 text-[#5f4737] font-sans">
            独家姓名学 AI 大模型详细解读：更专业、更详细、更清晰，会对每个数理、每个人生阶段、健康情况、音形意等详细解释还会做出总评分与改名建议。
          </p>
          <p className="mt-2 text-left text-[13px] leading-6 text-[#8c735f] font-sans">
            实时 AI 生成，预计耗时约 {PROFESSIONAL_NAME_STUDY_TIME_ESTIMATE}{ageValue ? `，当前将按 ${formatAgeLabel(ageInput)} 重点校准人生阶段解读。` : '，建议填写年龄后再生成会更精准。'}
          </p>
          <div className="mt-5 flex justify-center">
            <button
              type="button"
              onClick={handleOpenProReport}
              className="inline-flex items-center justify-center rounded-full bg-[linear-gradient(135deg,#6f4318,#b5792b,#d7ab63)] px-8 py-3 text-[15px] font-semibold text-white shadow-[0_16px_28px_rgba(122,74,17,0.24)] transition-transform hover:-translate-y-0.5"
            >
              AI 专业解析报告
            </button>
          </div>
          <p className="mt-2 text-center text-[14px] leading-7 text-[#7a6658] font-sans">
            <a
              href={getSancaiLiugeUrl('proReport', 'demo')}
              onClick={(event) => {
                event.preventDefault();
                openSampleProReport();
              }}
              className="font-semibold text-[#8b571c] underline decoration-[#d7a55f] underline-offset-4"
            >
              查看更完整的专业版姓名学报告样例
            </a>
            。
          </p>
        </div>

        <div className="space-y-1 mb-6">
          {result.warnings.map((item) => (
            <p key={item} className={`text-xs text-[#5A4535] font-sans`}>{item}</p>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-export-hide>
          <button
            onClick={() => void exportImage(resultRef, '三才六格')}
            disabled={isExporting}
            className="py-3 rounded-xl border border-[#E8DFD1] bg-white text-[#4A3C31] font-semibold hover:bg-[#FAF8F2] transition-colors flex items-center justify-center gap-2 disabled:opacity-60 col-span-2 sm:col-span-1"
          >
            <Download className={ICON_SIZE} /> {isExporting ? '生成中...' : '下载'}
          </button>
          <button
            onClick={handleReset}
            className="py-3 rounded-xl border border-[#E8DFD1] bg-white text-[#4A3C31] font-semibold hover:bg-[#FAF8F2] transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw className={ICON_SIZE} /> 重新输入
          </button>
          <button
            onClick={() => setView('records')}
            className="py-3 rounded-xl border border-[#E8DFD1] bg-white text-[#4A3C31] font-semibold hover:bg-[#FAF8F2] transition-colors flex items-center justify-center gap-2"
          >
            <History className="w-4 h-4" /> 记录列表
          </button>
        </div>
        {exportError ? (
          <p className="mt-3 text-center text-xs text-rose-600" data-export-hide>{exportError}</p>
        ) : null}

        <XuanYuanReportFooter
          qrDataUrl={reportQrDataUrl}
          textClassName="text-[#8B6B5A]"
          emphasizeClassName="text-[#7A4E31]"
          qrBorderClassName="border-[#d9ccb2]"
        />
        </div>
      </div>
      </div>
    );
  };

  const renderProReport = () => {
    const activeResult = activeProResult;
    if (!activeResult) return null;
    return (
      <div ref={proReportRef}>
        <SancaiLiugeProReportPage
          result={activeResult}
          isDemo={proReportMode === 'demo'}
          reportData={proReportData}
          reportError={proReportError}
          currentAge={activeProAge}
          etaLabel={PROFESSIONAL_NAME_STUDY_TIME_ESTIMATE}
          reportQrDataUrl={reportQrDataUrl}
          nameFrameNode={<NameFrameDiagram payload={buildFramePayloadFromResult(activeResult)} />}
          showRenameConsult={showRenameConsult}
          onBack={() => setView(result ? 'result' : 'input')}
          onOpenProExport={handleOpenProExport}
          onRetry={() => {
            setProReportCacheKey('');
            setProReportError('');
          }}
          onToggleRenameConsult={() => setShowRenameConsult((prev) => !prev)}
        />
      </div>
    );
  };

  const renderProExport = () => {
    const activeResult = activeProResult;
    if (!activeResult) return null;
    return (
      <div className="min-h-screen bg-[linear-gradient(180deg,#1d1713_0%,#33261d_35%,#f2eadf_35%,#fbf8f3_100%)]">
        <XuanYuanTopBar
          title="轩辕国学·导出专业报告"
          onBack={() => setView('proReport')}
          variant="light"
          maxWidthClassName="max-w-5xl"
        />
        <div className="mx-auto max-w-5xl px-4 py-6 pb-24">
          <div ref={proExportRef} className="overflow-hidden rounded-[32px] border border-[#d8c1a0] bg-[linear-gradient(180deg,#fffaf2_0%,#f6efe4_100%)] shadow-[0_24px_60px_rgba(32,22,13,0.28)]">
            <div className="border-b border-[#e2cfb3] bg-[radial-gradient(circle_at_top_right,rgba(255,219,140,0.45),transparent_34%),linear-gradient(135deg,#33261d,#5a4331)] px-6 py-12 text-white sm:px-10">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-1.5 text-xs tracking-[0.22em]">
                <Crown className="h-4 w-4" />
                PREMIUM EXPORT
              </div>
              <h1 className={`mt-4 text-[34px] font-bold tracking-[0.1em] ${KAITI_FONT}`}>姓名学专业报告封面</h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-white/85">比基础报告更奢华、更适合分享与留存，适合作为高级版咨询展示页或导出底稿。</p>
            </div>
            <div className="grid gap-6 px-6 py-8 sm:px-10 md:grid-cols-[1.3fr,0.9fr]">
              <div className="rounded-[24px] border border-[#ead9c3] bg-white/85 p-6">
                <p className="text-xs tracking-[0.22em] text-[#9b6d31]">姓名档案</p>
                <h2 className={`mt-2 text-[30px] font-bold tracking-[0.18em] text-[#4a3527] ${KAITI_FONT}`}>{activeResult.fullName}</h2>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl bg-[#faf3e8] px-4 py-3">
                    <p className="text-xs text-[#8b7157]">人格</p>
                    <p className="mt-1 text-2xl font-bold text-[#523b2a]">{activeResult.grids.renge.value81}</p>
                  </div>
                  <div className="rounded-2xl bg-[#faf3e8] px-4 py-3">
                    <p className="text-xs text-[#8b7157]">总格</p>
                    <p className="mt-1 text-2xl font-bold text-[#523b2a]">{activeResult.grids.zongge.value81}</p>
                  </div>
                  <div className="rounded-2xl bg-[#faf3e8] px-4 py-3">
                    <p className="text-xs text-[#8b7157]">三才</p>
                    <p className="mt-1 text-lg font-bold text-[#523b2a]">{activeResult.threeTalents.sequence}</p>
                  </div>
                  <div className="rounded-2xl bg-[#faf3e8] px-4 py-3">
                    <p className="text-xs text-[#8b7157]">建议评分</p>
                    <p className="mt-1 text-lg font-bold text-[#8b571c]">86 / 100</p>
                  </div>
                </div>
                <div className="mt-5 rounded-[20px] border border-[#eadbc7] bg-[#fffdf9] p-4 text-[15px] leading-7 text-[#5f4738]">
                  专业导出页主打留存与展示质感，建议与高阶 AI 解析正文配套使用。页面结构简洁，但视觉更稳、更贵气，便于咨询、截图、分享与老师二次讲解。
                </div>
              </div>
              <div className="rounded-[24px] border border-[#ead9c3] bg-white/85 p-6">
                <p className="text-xs tracking-[0.22em] text-[#9b6d31]">导出说明</p>
                <ul className="mt-4 space-y-3 text-[15px] leading-7 text-[#5f4738]">
                  <li>包含高阶封面、关键数理、总评分、咨询入口与品牌二维码。</li>
                  <li>视觉层次比基础简报更克制，但材质感与高端感更强。</li>
                  <li>适合后续继续扩展为 PDF、长图或咨询单页。</li>
                </ul>
                <div className="mt-6 rounded-[20px] border border-[#ecd7bc] bg-[#fffdf8] p-4 text-center shadow-sm">
                  <img src={PRO_TEACHER_QR_SRC} alt="若轩老师微信二维码" className="mx-auto h-[140px] w-[140px] rounded-xl bg-white object-contain" />
                  <p className="mt-3 text-sm font-semibold text-[#6d4e2f]">若轩老师微信：{PRO_TEACHER_WECHAT}</p>
                  <p className="mt-1 text-xs text-[#8b735c]">有具体改名问题，请直接扫码咨询</p>
                </div>
              </div>
            </div>
            <div className="border-t border-[#ead9c3] px-6 py-6 sm:px-10">
              <XuanYuanReportFooter
                qrDataUrl={reportQrDataUrl}
                textClassName="text-[#8B6B5A]"
                emphasizeClassName="text-[#7A4E31]"
                qrBorderClassName="border-[#d9ccb2]"
              />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3" data-export-hide>
            <button
              type="button"
              onClick={() => void exportImage(proExportRef, '三才六格_专业导出')}
              disabled={isExporting}
              className="inline-flex items-center gap-2 rounded-full bg-[#2f241b] px-5 py-3 text-sm font-semibold text-white shadow-[0_12px_24px_rgba(47,36,27,0.18)] disabled:opacity-60"
            >
              <Download className="h-4 w-4" />
              {isExporting ? '导出中...' : '导出专业报告'}
            </button>
            <button
              type="button"
              onClick={() => setView('proReport')}
              className="inline-flex items-center gap-2 rounded-full border border-[#d7c3ab] bg-white px-5 py-3 text-sm font-semibold text-[#5e4638]"
            >
              返回专业报告
            </button>
          </div>
          {exportError ? <p className="mt-3 text-sm text-rose-600">{exportError}</p> : null}
        </div>
      </div>
    );
  };

  const renderRecords = () => (
    <div className="min-h-screen bg-[#FAF8F2]">
      <XuanYuanTopBar
        title="轩辕国学·姓名学档案"
        onBack={() => setView(result ? 'result' : 'input')}
        variant="light"
        maxWidthClassName="max-w-3xl"
      />
      <div className="max-w-3xl mx-auto px-4 py-5 pb-20">

      {records.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center text-slate-500">暂无记录</div>
      ) : (
        <div className="space-y-3">
          {records.map((record) => (
            <div
              key={record.id}
              role="button"
              tabIndex={0}
              onClick={() => openRecord(record)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openRecord(record);
                }
              }}
              className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 transition-colors hover:border-[#d2ba94] hover:bg-[#fffdf8]"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-lg font-semibold text-slate-900">
                    {record.surname}{record.givenName}（{record.gender === 'male' ? '男' : '女'}）
                  </p>
                  <p className="mt-1 text-xs text-slate-500">{formatDate(record.updatedAt || record.createdAt)}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-600">
                    {(['tiange', 'renge', 'dige', 'waige', 'biange', 'zongge'] as GridResult['key'][]).map((key, index) => {
                      const grid = record.result.grids[key];
                      const rating = getGridChartLuckText(grid);
                      return (
                        <React.Fragment key={`${record.id}-${key}`}>
                          <span>
                            {getShortGridLabel(key)}
                            {' '}
                            <span className={`font-semibold ${getRatingColor(rating)}`}>{grid.value81}</span>
                          </span>
                          {index < 5 ? <span className="text-slate-300">·</span> : null}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>

                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    removeRecord(record.id);
                  }}
                  className="shrink-0 rounded-lg border border-rose-200 p-2 text-rose-600 hover:bg-rose-50"
                  aria-label={`删除 ${record.surname}${record.givenName} 的记录`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          <button
            onClick={clearRecords}
            className="w-full py-2 rounded-lg border border-slate-300 text-slate-600 text-sm hover:bg-slate-100"
          >
            清空记录
          </button>
        </div>
      )}
      </div>
    </div>
  );

  let content: React.ReactNode = renderInput();
  if (view === 'result') content = renderResult();
  if (view === 'proReport') content = renderProReport();
  if (view === 'proExport') content = renderProExport();
  if (view === 'records') content = renderRecords();

  return (
    <>
      {content}
      {showProUnlockModal ? (
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/45 p-4 backdrop-blur-[2px]">
          <div className="relative w-full max-w-md rounded-[24px] border border-[#e0c79f] bg-[linear-gradient(180deg,#fffaf2,#f7eddc)] p-5 shadow-[0_30px_80px_-45px_rgba(41,27,9,0.72)]">
            <button
              type="button"
              onClick={closeProfessionalUnlock}
              className="absolute right-3 top-3 rounded-full p-2 text-[#8a6a40] transition-colors hover:bg-white/70"
              aria-label="关闭专业姓名学激活弹窗"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="pr-8">
              <p className={`text-[20px] font-bold text-[#4d3424] ${KAITI_FONT}`}>专业姓名学</p>
              <p className="mt-3 text-[14px] leading-7 text-[#60483a] font-sans">
                请输入激活码解锁专业姓名学报告，激活码请添加若轩老师微信{PRO_TEACHER_WECHAT}。
              </p>
              <div className="mt-4 flex items-center gap-2 text-sm text-[#7b5d3f]">
                <span>若轩老师微信</span>
                <button
                  type="button"
                  onClick={() => void copyTeacherWechat()}
                  className="inline-flex items-center gap-1 font-semibold text-[#8b571c]"
                >
                  {PRO_TEACHER_WECHAT}
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="mt-4 space-y-3">
                <input
                  type="text"
                  value={proUnlockCode}
                  onChange={(event) => setProUnlockCode(event.target.value)}
                  placeholder="请输入激活码"
                  className="w-full rounded-xl border border-[#d9c2a0] bg-white px-3 py-3 text-[15px] text-[#4d3424] outline-none ring-0 placeholder:text-[#b39a80] focus:border-[#b57a37]"
                />
                <button
                  type="button"
                  onClick={() => void handleProUnlockSubmit()}
                  disabled={proUnlockLoading}
                  className="w-full rounded-xl bg-[linear-gradient(135deg,#6f4318,#b5792b,#d7ab63)] px-4 py-3 text-[15px] font-semibold text-white shadow-[0_14px_26px_rgba(122,74,17,0.22)] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {proUnlockLoading ? '提交中...' : '立即解锁'}
                </button>
              </div>
              {proUnlockNotice ? (
                <div className="mt-3 rounded-xl border border-[#ecd3b1] bg-white/85 px-3 py-2 text-sm leading-6 text-[#7b5d3f]">
                  {proUnlockNotice}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      {proReportLoading && view === 'proReport' ? (
        <AiTaskProgressOverlay
          title="AI 正在生成专业姓名学报告"
          etaLabel={PROFESSIONAL_NAME_STUDY_TIME_ESTIMATE}
          progressPercent={proReportProgressPercent}
          steps={[...PROFESSIONAL_NAME_STUDY_PROGRESS_STEPS]}
          stepIndex={proReportProgressIndex}
        />
      ) : null}
    </>
  );
};

export default SancaiLiuge;
